import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  IconButton,
  Stack,
  LinearProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import logoImage from '../assets/logo2.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

function getPasswordStrength(pwd) {
  if (!pwd) return { score: 0, label: '', color: 'inherit' };
  let score = 0;
  if (pwd.length >= 6) score += 1;
  if (pwd.length >= 10) score += 1;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score += 1;
  if (/\d/.test(pwd)) score += 1;
  if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
  const map = [
    { label: 'Muy débil', color: 'error' },
    { label: 'Débil', color: 'error' },
    { label: 'Regular', color: 'warning' },
    { label: 'Buena', color: 'info' },
    { label: 'Fuerte', color: 'success' },
    { label: 'Excelente', color: 'success' },
  ];
  return { score, ...map[score] };
}

function ChangePasswordPage() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();
  const { user, checkAuthStatus, isAuthenticated, isLoading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated) {
        navigate('/login', { replace: true });
      } else if (user && !user.isTemporaryAccount) {
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, user, authLoading, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden. Por favor, inténtelo de nuevo.');
      return;
    }

    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/change-password`, {
        newPassword,
        confirmPassword,
      });

      if (response.status === 200) {
        setSuccess('Contraseña cambiada con éxito. Su cuenta ya no es temporal.');
        checkAuthStatus();
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      } else {
        setError(response.data.message || 'Error al cambiar la contraseña.');
      }
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.response?.data?.message || 'Error al conectar con el servidor para cambiar la contraseña.');
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  const strength = getPasswordStrength(newPassword);
  const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ mt: { xs: 4, md: 8 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Card sx={{ width: '100%', overflow: 'hidden' }}>
          <Box
            sx={{
              p: 3,
              bgcolor: 'primary.main',
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              borderBottom: '3px solid',
              borderBottomColor: 'secondary.main',
            }}
          >
            <Box component="img" src={logoImage} alt="Kepler" sx={{ height: 44 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Kepler Approval Board
              </Typography>
              <Typography variant="caption" sx={{ color: 'grey.300' }}>
                Cambio de contraseña requerido
              </Typography>
            </Box>
          </Box>

          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: 'secondary.main',
                  color: 'common.white',
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  mb: 2,
                }}
              >
                <LockOutlinedIcon />
              </Box>
              <Typography variant="h5" align="center" sx={{ fontWeight: 700 }}>
                Establece tu nueva contraseña
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                Tu cuenta tiene una contraseña temporal. Crea una nueva para continuar.
              </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  required
                  fullWidth
                  name="newPassword"
                  label="Nueva contraseña"
                  type={showNew ? 'text' : 'password'}
                  id="newPassword"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNew(!showNew)} edge="end" size="small">
                          {showNew ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {newPassword && (
                  <Box>
                    <LinearProgress
                      variant="determinate"
                      value={(strength.score / 5) * 100}
                      color={strength.color}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ mt: 0.5, display: 'block', color: `${strength.color}.main`, fontWeight: 600 }}
                    >
                      Seguridad: {strength.label}
                    </Typography>
                  </Box>
                )}

                <TextField
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirmar contraseña"
                  type={showConfirm ? 'text' : 'password'}
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  error={confirmPassword.length > 0 && !passwordsMatch}
                  helperText={
                    confirmPassword.length > 0 && !passwordsMatch
                      ? 'Las contraseñas no coinciden'
                      : passwordsMatch
                      ? 'Las contraseñas coinciden'
                      : ''
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end" size="small">
                          {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ position: 'relative', mt: 2 }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="secondary"
                    size="large"
                    sx={{ py: 1.4, fontWeight: 700 }}
                    disabled={isLoading || !newPassword || !confirmPassword || !passwordsMatch}
                  >
                    Cambiar contraseña
                  </Button>
                  {isLoading && (
                    <CircularProgress
                      size={24}
                      color="secondary"
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-12px',
                        marginLeft: '-12px',
                      }}
                    />
                  )}
                </Box>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}

export default ChangePasswordPage;
